import type React from 'react';
import {
  getTypographyPreset,
  resolveTypographyPresetKey,
  resolveTypographyStyle,
  type DesignSystemTypography,
  type TypographyPreset,
  type TypographyPresetKey,
} from '../../lib/typography';

export type HeroSlotRole =
  | 'heading'
  | 'subheading'
  | 'primary-cta'
  | 'secondary-cta'
  | 'micro-line'
  | 'background';

export function extractLargestPixelFontSize(value: React.CSSProperties['fontSize']) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const matches = Array.from(value.matchAll(/(-?\d+(?:\.\d+)?)px/g));
  const sizes = matches
    .map((match) => Number(match[1]))
    .filter((size) => Number.isFinite(size) && size > 0);
  return sizes.length > 0 ? Math.max(...sizes) : null;
}

export function resolveCanvasHeadingFitStyle(args: {
  text: string;
  textStyle: React.CSSProperties;
  blockWidthPx: number;
  isHeading: boolean;
}): React.CSSProperties {
  if (!args.isHeading || args.blockWidthPx <= 0) return {};
  const configuredFontSize = extractLargestPixelFontSize(args.textStyle.fontSize);
  if (!configuredFontSize || configuredFontSize < 72) return {};

  const normalizedText = args.text.replace(/\s+/g, ' ').trim();
  if (!normalizedText) return {};

  const longestWordLength = normalizedText
    .split(/\s+/)
    .reduce((max, word) => Math.max(max, word.length), 1);
  const desiredLineCount = normalizedText.length > 24 ? 2 : 1;
  const targetCharsPerLine = Math.max(
    longestWordLength,
    Math.ceil(normalizedText.length / desiredLineCount)
  );
  const availableWidth = Math.max(1, args.blockWidthPx * 0.96);
  const estimatedMaxFontSize = Math.floor(availableWidth / (targetCharsPerLine * 0.56));
  const fittedFontSize = Math.max(36, Math.min(configuredFontSize, estimatedMaxFontSize));

  return fittedFontSize < configuredFontSize
    ? {
        fontSize: `${fittedFontSize}px`,
      }
    : {};
}

export function getHeroFontClass(font?: string) {
  switch (font) {
    case 'serif':
      return 'font-serif';
    case 'mono':
      return 'font-mono';
    case 'cormorant':
      return 'font-cormorant';
    case 'inter':
      return 'font-inter';
    case 'playfair':
      return 'font-playfair';
    default:
      return 'font-sans';
  }
}

export function getHeroButtonSizeClass(size?: string) {
  switch (size) {
    case 'small':
      return 'px-4 py-2 text-sm';
    case 'large':
      return 'px-8 py-4 text-lg';
    default:
      return 'px-6 py-3';
  }
}

export function getHeroButtonStyleClass(style?: string, isPrimary = true) {
  const baseClass =
    'inline-flex items-center justify-center rounded-full font-medium tracking-tight transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-black/30';
  switch (style) {
    case 'outlined':
      return `${baseClass} border border-white/40 bg-white/5 text-white hover:bg-white hover:text-stone-900 hover:border-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)]`;
    case 'ghost':
      return `${baseClass} text-white/95 hover:bg-white/10`;
    default:
      if (isPrimary) {
        return `${baseClass} bg-white text-stone-900 hover:bg-stone-100 shadow-lg shadow-black/15`;
      }
      return `${baseClass} border border-white/40 bg-white/5 text-white hover:bg-white hover:text-stone-900 hover:border-white`;
  }
}

export function resolveHeroSlotTypographyStyle(
  blockContent: Record<string, unknown> | undefined,
  role: HeroSlotRole,
  designSystem: Partial<DesignSystemTypography> | undefined,
  fontLibrary: Record<string, string>,
  options?: {
    fluid?: boolean;
  }
) {
  const presetFallbackByRole: Record<HeroSlotRole, TypographyPresetKey> = {
    heading: 'heroTitle',
    subheading: 'heroSubtitle',
    'micro-line': 'heroMicroLine',
    'primary-cta': 'button',
    'secondary-cta': 'button',
    background: 'body',
  };
  const presetKey = resolveTypographyPresetKey(
    blockContent?.presetKey,
    presetFallbackByRole[role]
  );
  const preset = getTypographyPreset(designSystem, presetKey);
  const tunedPreset: TypographyPreset = {
    ...preset,
    size: {
      mobile:
        role === 'heading'
          ? Math.max(48, preset.size.mobile)
          : role === 'subheading'
            ? Math.max(20, preset.size.mobile)
            : preset.size.mobile,
      desktop:
        role === 'heading'
          ? Math.max(88, preset.size.desktop)
          : role === 'subheading'
            ? Math.max(32, preset.size.desktop)
            : preset.size.desktop,
    },
  };
  const useFluidSizing = options?.fluid !== false;
  let style = resolveTypographyStyle(tunedPreset, fontLibrary, {
    fluid: useFluidSizing,
  });
  const overrideRaw =
    blockContent?.override && typeof blockContent.override === 'object'
      ? (blockContent.override as Record<string, unknown>)
      : null;
  if (overrideRaw && overrideRaw.mode === 'custom') {
    const nextStyle: React.CSSProperties = { ...style };
    if (typeof overrideRaw.fontFamilyKey === 'string' && fontLibrary[overrideRaw.fontFamilyKey]) {
      nextStyle.fontFamily = fontLibrary[overrideRaw.fontFamilyKey];
    }
    if (Number.isFinite(overrideRaw.fontWeight)) {
      nextStyle.fontWeight = Math.max(300, Math.min(800, Number(overrideRaw.fontWeight)));
    }
    if (overrideRaw.fontStyle === 'italic' || overrideRaw.fontStyle === 'normal') {
      nextStyle.fontStyle = overrideRaw.fontStyle;
    }
    const mobileSize = Number(overrideRaw.sizeMobile);
    const desktopSize = Number(overrideRaw.sizeDesktop);
    if (Number.isFinite(mobileSize) || Number.isFinite(desktopSize)) {
      const m = Number.isFinite(mobileSize) ? mobileSize : tunedPreset.size.mobile;
      const d = Number.isFinite(desktopSize) ? desktopSize : tunedPreset.size.desktop;
      const minViewport = 375;
      const maxViewport = 1280;
      const delta = Math.max(0, d - m);
      const viewportSpan = maxViewport - minViewport;
      const fluidExpression = `calc(${m}px + ${delta} * ((100vw - ${minViewport}px) / ${viewportSpan}))`;
      nextStyle.fontSize = useFluidSizing
        ? `clamp(${m}px, ${fluidExpression}, ${d}px)`
        : `${d}px`;
    }
    if (typeof overrideRaw.color === 'string' && overrideRaw.color.trim()) {
      nextStyle.color = overrideRaw.color.trim();
    }
    style = nextStyle;
  }
  return style;
}
